import styles from "./toolbox-point-style.module.css";
import {
  PointCoordinate,
  PointIconName,
} from "PosterEditor/model/poster-types";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { PointStyle } from "PosterEditor/model/poster-types";
import { ToolboxReverseGeocodeSuggestions } from "PosterEditor/sidebar/toolboxes/pre-made-toolboxes/toolbox-reverse-geocode-suggestions/toolbox-reverse-geocode-suggestions";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import { ToolboxComponentSection as Section } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxComponentPointIconGrid } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-point-icon-grid";
import { ToolboxSectionDivider as Divider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxComponentColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { ToolboxComponentCheckbox } from "../../toolbox-components/toolbox-component-checkbox";
import { useEffect, useState } from "react";
import { lineColors, StyledPoint } from "PosterEditor/model/poster-project";
import { PointIcon } from "PosterEditor/common-components/point-icon";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { DeleteButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { PointEditingPermissions } from "PosterEditor/model/editing-permissions";

interface ToolboxPointStyleProps {
  point: StyledPoint;
}

export const ToolboxPointStyle = (props: ToolboxPointStyleProps) => {
  const { setEditingElement } = useEditorUI();
  const { project, isTemplateEditing } = usePosterProject();
  const { deleteCoordinate, setPointStyle, removeAllStyledPoints } =
    usePosterProject();
  const selectedCoordinate = props.point.coordinate;

  const [editingStyle, setEditingStyle] = useState<PointStyle>(
    props.point.style
  );

  const selectedPoint = props.point;

  useEffect(() => {
    setPointStyle(selectedCoordinate, editingStyle);
  }, [editingStyle]);

  useEffect(() => {
    setEditingStyle(props.point.style);
  }, [props.point]);

  const deleteSelectedPoint = () => {
    deleteCoordinate(selectedPoint.coordinate);
    setEditingElement(undefined);
  };

  const setIconName = (icon: PointIconName) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        icon: icon,
      };
    });
  };

  const setMainColor = (color: string) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        mainColor: color,
      };
    });
  };

  const setSecondaryColor = (color: string) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        secondaryColor: color,
      };
    });
  };

  const setSize = (size: number) => {
    setEditingStyle((prev) => {
      return {
        ...prev,
        size: size,
      };
    });
  };

  const removeStyleFromSelectedPoint = () => {
    if (selectedPoint?.coordinate) {
      setPointStyle(selectedPoint?.coordinate, undefined);
    }
  };

  const onSelectLandmarkText = (landmarkText: string) => {};

  const allowEditingIcon =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.icon ??
      project.editingPermissions.point.icon);
  const allowEditingSize =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.size ??
      project.editingPermissions.point.size);
  const allowEditingLineColor =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.lineColor ??
      project.editingPermissions.point.lineColor);
  const allowEditingFillColor =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.fillColor ??
      project.editingPermissions.point.fillColor);
  const allowDeletingPoint =
    isTemplateEditing ||
    (selectedPoint.editingPermissions?.delete ??
      project.editingPermissions.point.delete);

  return (
    <div className={styles.container}>
      <Header
        allowDelete={allowDeletingPoint}
        onClickDelete={deleteSelectedPoint}
        point={selectedPoint}
      />
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          {allowEditingIcon && (
            <Section>
              <ToolboxComponentPointIconGrid
                selectedIcon={editingStyle.icon}
                onSelectIcon={setIconName}
              />
              <Divider />
            </Section>
          )}
          {(allowEditingFillColor || allowEditingLineColor) && (
            <Section>
              <ColorControls
                mainColor={editingStyle.mainColor}
                secondaryColor={editingStyle.secondaryColor ?? "white"}
                onMainColorChange={setMainColor}
                onSecondaryColorChange={setSecondaryColor}
              />
              <Divider />
            </Section>
          )}
          {allowEditingSize && (
            <Section>
              <Slider
                title="Size"
                min={10}
                max={80}
                value={editingStyle.size}
                onChange={setSize}
              />
            </Section>
          )}
          {isTemplateEditing && (
            <>
              <Divider />
              <Admin point={selectedPoint} />
            </>
          )}
        </div>
      </Toolbox>
    </div>
  );
};

interface HeaderProps {
  point: StyledPoint;
  allowDelete: boolean;
  onClickDelete: () => void;
}

const Header = (props: HeaderProps) => {
  const selectedCoordinate = props.point.coordinate;

  const textColor = getComputedStyle(document.documentElement).getPropertyValue(
    "--color-text-secondary"
  );

  const latLongString = `${selectedCoordinate.lat.toFixed(
    6
  )}, ${selectedCoordinate.lng.toFixed(6)}`;
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        {props.point.style.icon && (
          <PointIcon
            iconName={props.point.style.icon}
            color={textColor}
            size={20}
          />
        )}
        <h1> {latLongString}</h1>
      </div>
      {props.allowDelete && (
        <div className={styles.headerTrailing}>
          <DeleteButton onClick={props.onClickDelete} />
        </div>
      )}
    </div>
  );
};

interface ColorControlsProps {
  mainColor: string;
  secondaryColor: string;
  onMainColorChange: (color: string) => void;
  onSecondaryColorChange: (color: string) => void;
}

const ColorControls = (props: ColorControlsProps) => {
  const [isMainColorExpanded, setIsMainColorExpanded] =
    useState<boolean>(false);
  const [isSecondaryColorExpanded, setIsSecondaryColorExpanded] =
    useState<boolean>(false);

  return (
    <div className={styles.colorContainer}>
      <ToolboxComponentColorPicker
        title="Line color"
        colors={lineColors}
        selectedColor={props.mainColor}
        onSelectColor={props.onMainColorChange}
        showSwatch={true}
        isExpanded={isMainColorExpanded}
        onToggleExpand={() => {
          setIsMainColorExpanded(!isMainColorExpanded);
        }}
      />
      <ToolboxComponentColorPicker
        title="Fill color"
        colors={lineColors}
        selectedColor={props.secondaryColor}
        onSelectColor={props.onSecondaryColorChange}
        showSwatch={true}
        isExpanded={isSecondaryColorExpanded}
        onToggleExpand={() => {
          setIsSecondaryColorExpanded(!isSecondaryColorExpanded);
        }}
      />
    </div>
  );
};

interface AdminProps {
  point: StyledPoint;
}

const Admin = (props: AdminProps) => {
  const { project, setStyledPointEditingPermissions } = usePosterProject();

  const [permissions, setPermissions] = useState<PointEditingPermissions>(
    props.point.editingPermissions ?? project.editingPermissions.point
  );

  useEffect(() => {
    setPermissions(
      props.point.editingPermissions ?? project.editingPermissions.point
    );
  }, [props.point]);

  const updatePermissions = (
    key: keyof PointEditingPermissions,
    value: boolean
  ) => {
    const newPermissions = {
      ...permissions,
      [key]: value,
    };
    setPermissions(newPermissions);
    setStyledPointEditingPermissions(props.point, newPermissions);
  };

  return (
    <Section title="Editing permissions">
      {(Object.keys(permissions) as (keyof PointEditingPermissions)[]).map(
        (key) => (
          <ToolboxComponentCheckbox
            key={key}
            text={key.charAt(0).toUpperCase() + key.slice(1)}
            isSelected={permissions[key] === true}
            onClick={() => {
              const newValue = !permissions[key];
              updatePermissions(key, newValue);
            }}
          />
        )
      )}
    </Section>
  );
};
